import type { FC, HTMLAttributes, ReactNode } from 'react';
import type { RichTextField } from '@prismicio/client';
import { PrismicRichText as BasePrismicRichText } from '@prismicio/react';
import type { RTLinkNode } from '@prismicio/types';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';

import { cn } from '@/lib/utils';

import H1 from '@/components/atoms/Typography/H1';
import H2 from '@/components/atoms/Typography/H2';
import H3 from '@/components/atoms/Typography/H3';
import H4 from '@/components/atoms/Typography/H4';
import H5 from '@/components/atoms/Typography/H5';
import H6 from '@/components/atoms/Typography/H6';

type defaultComponentsProps = {
    heading1?: (props: { children?: ReactNode }) => JSX.Element;
    heading2?: (props: { children?: ReactNode }) => JSX.Element;
    heading3?: (props: { children?: ReactNode }) => JSX.Element;
    heading4?: (props: { children?: ReactNode }) => JSX.Element;
    heading5?: (props: { children?: ReactNode }) => JSX.Element;
    heading6?: (props: { children?: ReactNode }) => JSX.Element;
    paragraph?: (props: { children?: ReactNode }) => JSX.Element;
    list?: (props: { children?: ReactNode }) => JSX.Element;
    listItem?: (props: { children?: ReactNode }) => JSX.Element;
    oList?: (props: { children?: ReactNode }) => JSX.Element;
    oListItem?: (props: { children?: ReactNode }) => JSX.Element;
    hyperlink?: (props: {
        node?: RTLinkNode;
        children?: ReactNode;
    }) => JSX.Element;
};

export interface RichTextProps
    extends HTMLAttributes<HTMLDivElement>,
        VariantProps<typeof richTextVariant> {
    field?: RichTextField | null | undefined;
    size?: '2xs' | 'xs' | 'sm' | null;
    components?: defaultComponentsProps;
}

const richTextVariant = cva('', {
    variants: {
        darkmode: {
            true: 'text-white',
            false: 'text-black-high-contrast',
        },
        textAlign: {
            left: 'text-left',
            center: 'text-center',
            right: 'text-right',
        },
    },
    defaultVariants: {
        darkmode: false,
        textAlign: 'left',
    },
});

const pTextVariants = cva('', {
    variants: {
        size: {
            '2xs': 'text-2xs',
            xs: 'text-xs',
            sm: 'text-sm',
        },
    },
    defaultVariants: {
        size: 'sm',
    },
});

const defaultComponents = ({
    textAlign,
    darkmode,
    size,
}: {
    textAlign?: 'left' | 'center' | 'right' | null;
    darkmode?: boolean | null;
    size?: '2xs' | 'xs' | 'sm' | null;
}): defaultComponentsProps => ({
    heading1: ({ children }) => <H1 className="pb-6">{children}</H1>,
    heading2: ({ children }) => <H2 className="pb-6">{children}</H2>,
    heading3: ({ children }) => <H3 className="pb-6">{children}</H3>,
    heading4: ({ children }) => <H4 className="pb-6">{children}</H4>,
    heading5: ({ children }) => <H5 className="pb-6">{children}</H5>,
    heading6: ({ children }) => <H6 className="pb-6">{children}</H6>,
    paragraph: ({ children }) => (
        <p
            className={cn(
                richTextVariant({ textAlign, darkmode }),
                pTextVariants({ size }),
                'my-0 break-words pb-6',
            )}
        >
            {children}
        </p>
    ),
    list: ({ children }) => (
        <ul
            className={cn(
                richTextVariant({ textAlign, darkmode }),
                'my-2 ml-4 list-disc py-0',
            )}
        >
            {children}
        </ul>
    ),
    listItem: ({ children }) => <li>{children}</li>,
    oList: ({ children }) => (
        <ol
            className={
                (cn(richTextVariant({ textAlign, darkmode })),
                'my-2 ml-4 list-decimal py-0')
            }
        >
            {children}
        </ol>
    ),
    oListItem: ({ children }) => <li>{children}</li>,
    hyperlink: ({ node, children }) => (
        <a
            href={node?.data?.url}
            className={cn(
                'cursor-pointer text-green-700 underline hover:text-green-600 hover:no-underline',
            )}
        >
            {children}
        </a>
    ),
});

const RichText: FC<RichTextProps> = ({
    components,
    textAlign,
    darkmode,
    size,
    field,
    ...props
}) => (
    <BasePrismicRichText
        field={field}
        components={{
            ...defaultComponents({ textAlign, darkmode, size }),
            ...components,
        }}
        {...props}
    />
);

export default RichText;
